/**
 * DRPX Courier — Gemischte Supabase-Funktionen
 *
 * Stripe-Checkout und -Kundenportal, öffentliches Tracking, Auftragsdokumente und
 * USt-IdNr.-Validierung (VIES).
 *
 * @module lib/supabase/misc
 */

import { _sb } from './client.js';
import {
  validateUploadFileDeep,
  sanitizeFileName,
  type UploadValidationError,
} from '../upload.js';

// ════════════════════════════════════════════════════════════════════
// Interne Helper
// ════════════════════════════════════════════════════════════════════

const NO_CLIENT_ERROR = new Error('Supabase client not initialized');

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/**
 * Parst die Edge-Function-Response, die entweder als String (JSON) oder
 * als bereits geparstes Objekt ankommen kann.
 */
function parseData(raw: unknown): Record<string, unknown> | null {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object') return raw as Record<string, unknown>;
  return null;
}

// ════════════════════════════════════════════════════════════════════
// Lokale Interfaces (nicht exportiert)
// ════════════════════════════════════════════════════════════════════

/**
 * Auftragsdokument nach Mapping (inline, kein Mapper in mappers.ts).
 */
export interface AuftragDokument {
  id: string;
  auftragId: string;
  uploaderId: string;
  uploaderName: string;
  typ: string;
  dateiName: string;
  dateiGroesse: number;
  filePath: string;
  notiz: string;
  createdAt: string;
}

/**
/**
 * Tracking-Daten (reduziert, keine sensiblen Felder).
 */
export interface TrackingData {
  /** Auftrag-ID (Fallback wenn `nr` leer). */
  id?: string;
  nr: string;
  status: string;
  abhol: string;
  ziel: string;
  datum: string;
  zeit: string;
  verlauf: Array<{ status?: string; zeit?: string; [key: string]: unknown }>;
  fahrzeug: string;
  createdAt: string;
}

/**
 * Ergebnis der VIES-Prüfung.
 */
interface VatCheckResult {
  valid: boolean;
  name?: string;
  address?: string;
  /**
   * true = Prüfung war technisch nicht möglich (Netzwerk, CORS, VIES down,
   * Rate-Limit). Die UI zeigt dann "Prüfung derzeit nicht möglich" statt
   * "USt-IdNr. ungültig". Rohe SDK-Fehlertexte gehen NIE an den User
   * (nur ins Console-Log).
   */
  technicalError?: boolean;
}

// ════════════════════════════════════════════════════════════════════
// STRIPE CHECKOUT
// ════════════════════════════════════════════════════════════════════

/** Ergebnis von Checkout/Kundenportal: Adresse zum Weiterleiten oder Fehlercode. */
export interface StripeWeiterleitung {
  url: string | null;
  /**
   * Fehlercode der Edge Function, z. B. `abo_vorhanden` (es gibt schon ein
   * Stripe-Abo → Kundenportal öffnen), `kein_stripe_kunde`, sonst Text.
   */
  fehler: string | null;
}

/**
 * Edge Function aufrufen, die `{ url }` liefert (Stripe-Checkout oder
 * -Kundenportal). Bei Status ≠ 2xx steckt unsere Antwort `{ error }` im
 * `context` des Fehlers.
 */
async function stripeAufruf(name: string, body: Record<string, unknown>): Promise<StripeWeiterleitung> {
  if (!_sb) return { url: null, fehler: NO_CLIENT_ERROR.message };
  try {
    const res = await _sb.functions.invoke(name, { body });
    if (res.error) {
      const ctx = (res.error as { context?: { json?: () => Promise<unknown> } }).context;
      if (ctx && typeof ctx.json === 'function') {
        try {
          const b = (await ctx.json()) as { error?: unknown } | null;
          if (b && typeof b.error === 'string') return { url: null, fehler: b.error };
        } catch { /* keine JSON-Antwort */ }
      }
      return { url: null, fehler: res.error.message || String(res.error) };
    }
    const data = parseData(res.data);
    if (data && typeof data.error === 'string') return { url: null, fehler: data.error };
    const url = typeof data?.url === 'string' ? data.url : '';
    return url ? { url, fehler: null } : { url: null, fehler: 'Keine Adresse von Stripe erhalten' };
  } catch (e: unknown) {
    return { url: null, fehler: errMsg(e) };
  }
}

/**
 * Startet den Stripe-Checkout für ein neues Abo (Lieferung 3b-2).
 * Die Rolle liest der Server selbst: Fahrer = DRPX-Standard, Firma =
 * DRPX-Plus oder -Pro (`tarif`). In der Gratisphase wird erst ab ihrem
 * Ende abgebucht.
 *
 * @param intervall 'monat' oder 'jahr'
 * @param tarif     nur Firma: 'plus' (Standard) oder 'pro'
 * @returns Checkout-Adresse oder Fehlercode (`abo_vorhanden` → Portal öffnen)
 */
export function createCheckoutSession(
  intervall: 'monat' | 'jahr',
  tarif?: 'plus' | 'pro',
): Promise<StripeWeiterleitung> {
  return stripeAufruf('create-checkout-session', tarif ? { interval: intervall, tarif } : { interval: intervall });
}

/**
 * Öffnet das Stripe-Kundenportal: Rechnungen, Zahlungsart, Adresse,
 * Tarif/Intervall wechseln, kündigen (zum Ende der bezahlten Laufzeit).
 *
 * @returns Portal-Adresse oder Fehlercode (`kein_stripe_kunde`)
 */
export function createPortalSession(): Promise<StripeWeiterleitung> {
  return stripeAufruf('create-portal-session', {});
}

// ════════════════════════════════════════════════════════════════════
// ÖFFENTLICHES TRACKING (kein Login nötig)
// ════════════════════════════════════════════════════════════════════

/**
 * Lädt Tracking-Daten für einen Auftrag über einen UUID-v4-Token.
 * Kein Login erforderlich — der Token ist der einzige Zugangsschutz.
 * Intern als RPC `get_public_tracking` implementiert, damit nur die
 * nicht-sensiblen Felder (Status, Städte, Verlauf) zurückgegeben werden.
 *
 * Direkter Aufruf (kein `api()`-Wrapper).
 */
export async function getPublicTracking(
  token: string,
): Promise<{ data: TrackingData | null; error?: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.rpc('get_public_tracking', { p_token: token });
    if (r.error) return { data: null, error: r.error };
    const d = r.data as Record<string, unknown> | null;
    if (!d || !d.found) return { data: null };
    return {
      data: {
        nr: typeof d.nr === 'string' ? d.nr : '',
        status: typeof d.status === 'string' ? d.status : '',
        abhol: typeof d.abhol_city === 'string' ? d.abhol_city : '',
        ziel: typeof d.ziel_city === 'string' ? d.ziel_city : '',
        datum: typeof d.datum === 'string' ? d.datum : '',
        zeit: typeof d.zeit === 'string' ? d.zeit : '',
        verlauf: Array.isArray(d.verlauf) ? d.verlauf : [],
        fahrzeug: typeof d.fahrzeug === 'string' ? d.fahrzeug : '',
        createdAt: typeof d.created_at === 'string' ? d.created_at : '',
      },
    };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// AUFTRAG-DOKUMENTE
// ════════════════════════════════════════════════════════════════════

/**
 * Mappt eine Datenbank-Row aus `auftrag_dokumente` in das App-Format.
 * Inline-Mapper (kein zentraler Mapper in `mappers.ts`), weil
 * `AuftragDokument` ein lokaler Typ ist.
 */
function mapDokumentRow(d: Record<string, unknown>): AuftragDokument {
  return {
    id: typeof d.id === 'string' ? d.id : '',
    auftragId: typeof d.auftrag_id === 'string' ? d.auftrag_id : '',
    uploaderId: typeof d.uploader_id === 'string' ? d.uploader_id : '',
    uploaderName: typeof d.uploader_name === 'string' ? d.uploader_name : '',
    typ: typeof d.typ === 'string' ? d.typ : '',
    dateiName: typeof d.datei_name === 'string' ? d.datei_name : '',
    dateiGroesse: typeof d.datei_groesse === 'number' ? d.datei_groesse : 0,
    filePath: typeof d.file_path === 'string' ? d.file_path : '',
    notiz: typeof d.notiz === 'string' ? d.notiz : '',
    createdAt: typeof d.created_at === 'string' ? d.created_at : '',
  };
}

/**
 * Lädt alle Dokumente, die zu einem Auftrag hochgeladen wurden
 * (Lieferscheine, Rechnungen, CMR-Frachtbriefe, sonstige Belege).
 *
 * Direkter Aufruf (kein `api()`-Wrapper).
 */
export async function getAuftragDokumente(
  auftragId: string,
): Promise<{ data: AuftragDokument[]; error?: unknown }> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('auftrag_dokumente')
      .select('*')
      .eq('auftrag_id', auftragId)
      .order('created_at', { ascending: false });
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    return { data: rows.map(mapDokumentRow) };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Lädt ein Dokument zu einem Auftrag hoch (Bucket `auftrag-dokumente`)
 * und legt einen entsprechenden DB-Eintrag an. Validiert Größe/MIME,
 * sanitiert Dateinamen.
 *
 * Wird über `api()` aufgerufen — Caller liest das ganze Dokument-Objekt.
 */
export async function uploadAuftragDokument(
  auftragId: string,
  userId: string,
  userName: string,
  typ: string,
  file: File,
  notiz?: string,
): Promise<{ data: AuftragDokument | null; error: UploadValidationError | string | null }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const valErr = await validateUploadFileDeep('auftrag-dokumente', file);
    if (valErr) return { data: null, error: valErr };
    const safeName = sanitizeFileName(file.name);
    const path = userId + '/' + auftragId + '/' + Date.now() + '_' + safeName;
    const r = await client.storage.from('auftrag-dokumente').upload(path, file);
    if (r.error) return { data: null, error: r.error.message };
    const d = {
      auftrag_id: auftragId,
      uploader_id: userId,
      uploader_name: userName,
      typ: typ || 'sonstiges',
      datei_name: file.name,
      datei_groesse: file.size,
      file_path: r.data.path,
      notiz: notiz || '',
    };
    const ins = await client.from('auftrag_dokumente').insert(d).select().single();
    if (ins.error) return { data: null, error: ins.error.message };
    return {
      data: mapDokumentRow(ins.data as Record<string, unknown>),
      error: null,
    };
  } catch (e: unknown) {
    return { data: null, error: errMsg(e) };
  }
}

/**
 * Löscht ein Auftrags-Dokument aus Storage und Datenbank.
 *
 * Wird über `api()` aufgerufen — daher `{data, error}`-Shape.
 *
 * **Bug-Fix gegenüber JS-Version:** Die alte Version gab `{error}` ohne
 * `data`-Feld zurück → `api()` gab immer `null` zurück → Caller brach
 * bei `if (!res) return;` ab → State-Update und Erfolgs-Toast waren
 * toter Code. Jetzt: `data: true` als Erfolgs-Signal.
 */
export async function deleteAuftragDokument(
  id: string,
  filePath?: string,
): Promise<{ data: true | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    if (filePath) {
      await client.storage.from('auftrag-dokumente').remove([filePath]);
    }
    const r = await client.from('auftrag_dokumente').delete().eq('id', id);
    if (r.error) return { data: null, error: r.error };
    return { data: true, error: null };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// USt-IdNr. VALIDIERUNG (EU VIES)
// ════════════════════════════════════════════════════════════════════

/**
 * Prüft eine USt-IdNr. gegen die offizielle EU-VIES-Datenbank.
 * Ruft die Edge Function `check-vat` auf.
 *
 * Direkter Aufruf (kein `api()`-Wrapper).
 */
export async function checkVat(vatId: string): Promise<VatCheckResult> {
  if (!_sb) return { valid: false, technicalError: true };
  const client = _sb;
  try {
    const res = await client.functions.invoke('check-vat', {
      body: JSON.stringify({ vatId: vatId }),
    });
    if (res.error) {
      // Technischer Fehler (CORS, Netzwerk, non-2xx wie Rate-Limit/VIES-down).
      // Rohe SDK-Meldung nur ins Log — User bekommt i18n-Text via Flag.
      console.warn('[checkVat]', res.error.message || String(res.error));
      return { valid: false, technicalError: true };
    }
    const data = parseData(res.data);
    if (data && data.error) {
      console.warn('[checkVat] Server-Fehler:', String(data.error));
      return { valid: false, technicalError: true };
    }
    return {
      valid: !!data?.valid,
      name: typeof data?.name === 'string' ? data.name : undefined,
      address: typeof data?.address === 'string' ? data.address : undefined,
    };
  } catch (e: unknown) {
    console.warn('[checkVat]', errMsg(e));
    return { valid: false, technicalError: true };
  }
}
